import React from "react";

/**
 * Small, reusable spinner component.
 * - Props:
 *   - size: number (px) default 40
 *   - thickness: number (px) border thickness default 4
 *   - color: string CSS color for the spinner accent
 *   - inline: boolean if true spinner is inline-block, otherwise block (centered)
 *
 * Usage:
 *   <Spinner />
 *   <Spinner size={24} thickness={3} color="#fff" inline />
 */

export default function Spinner({
  size = 40,
  thickness = 4,
  color = "#1976d2",
  inline = false,
}) {
  const display = inline ? "inline-block" : "block";
  const style = {
    width: size,
    height: size,
    borderRadius: "50%",
    border: `${thickness}px solid ${hexWithAlpha(color, 0.25)}`,
    borderTopColor: color,
    display,
    boxSizing: "border-box",
    animation: "zulal-spinner-spin 0.9s linear infinite",
  };

  return (
    <>
      <div style={style} role="status" aria-busy="true" aria-label="Loading" />
      <style>{`
        @keyframes zulal-spinner-spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

/* tiny helper to make a semi-transparent version of the color.
   If color is already rgba() or contains alpha, we leave it as-is. */
function hexWithAlpha(col, alpha) {
  if (!col) return `rgba(0,0,0,${alpha})`;
  // if already rgba/hsla, return as-is
  if (col.startsWith("rgba") || col.startsWith("hsla")) return col;
  if (col.startsWith("rgb") || col.startsWith("hsl")) return col;
  // if hex like #RRGGBB or #RGB
  const hex = col.replace("#", "");
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  // fallback
  return col;
}
